import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Avatar,
  Button,
  Dialog,
  DialogSurface,
  Text,
} from '@coreai/fluentui-react';
import { DismissRegular } from '@fluentui/react-icons';
import { useAuth } from '@/auth';
import { useAppStore } from '@/stores/appStore';
import { useTenants } from '@/hooks/useAzureResources';
import { useUserPhoto } from '@/hooks/useUserPhoto';
import {
  featureFlagConfig,
  isExperimentalFlagVisible,
  type FeatureFlagName,
} from '@/featureFlags';
import type { SettingsModalSection } from '@/hooks/useSettingsModal';
import { useSettingsModalStyles } from './SettingsModal.styles';
import { useSettingsSections } from './useSettingsSections';
import { getShortcutParts } from '@/utils/input';
import { SettingsNav } from './SettingsNav';
import { ProfileSection } from './sections/ProfileSection';
import { GeneralSection } from './sections/GeneralSection';
import { AppearanceSection } from './sections/AppearanceSection';
import { AccessibilitySection } from './sections/AccessibilitySection';
import { AiAssistantSection } from './sections/AiAssistantSection';
import { PrivacySection } from './sections/PrivacySection';
import { ExperimentalSection } from './sections/ExperimentalSection';
import { AboutSection } from './sections/AboutSection';

// Module-scope: featureFlagConfig is a frozen const, so the badge count
// shown next to "Experimental" never changes at runtime. Only flags that
// surface in the section (not marked `hidden`) count toward the badge.
// Hoisting avoids recomputing on every modal render.
const EXPERIMENTAL_FLAG_COUNT = (Object.keys(featureFlagConfig) as FeatureFlagName[]).filter(
  isExperimentalFlagVisible,
).length;

export interface SettingsModalProps {
  open: boolean;
  /** Optional section to scroll to when opened. Falls back to "general"
   *  (or the last viewed section if the modal stays mounted). */
  initialSection?: SettingsModalSection | null;
  onClose: () => void;
  /** Opens the existing TenantSwitchDialog (lives at root next to this
   *  modal in RootLayout). */
  onSwitchTenantClick: () => void;
  /** Opens the existing KeyboardShortcutsDialog. */
  onOpenShortcutsDialog: () => void;
}

/**
 * Unified Settings modal — split-pane Dialog inspired by Copilot CLI's
 * settings surface, grounded in Auto's existing stores and hooks.
 *
 * Sections:
 *   Account   — Profile (signed-in identity, active tenant, sign out)
 *   Workspace — General, Appearance, Accessibility, AI assistant, Privacy, Experimental
 *   About     — Auto Portal (replaces standalone AboutDialog)
 *
 * Profile is the only account-scoped pane; everything else is workspace-wide.
 * Project- and app-scoped settings (permissions, danger zone, env vars)
 * live on their own /settings routes, intentionally outside this modal so
 * the nav doesn't morph based on the user's current route.
 *
 * Triggered by the user-menu "Settings…" item or by ⌘, anywhere in the app
 * (see `useSettingsModal`).
 */
export const SettingsModal = ({
  open,
  initialSection,
  onClose,
  onSwitchTenantClick,
  onOpenShortcutsDialog,
}: SettingsModalProps) => {
  const styles = useSettingsModalStyles();
  const { account } = useAuth();
  const photo = useUserPhoto();
  // Subscribe per-field rather than destructuring the whole store, so
  // unrelated app-store changes (recent-work, selectedApp etc.) don't
  // re-render the modal shell. SettingsModal stays mounted in RootLayout,
  // so it would otherwise re-render on every navigation.
  const selectedTenantId = useAppStore((s) => s.selectedTenantId);
  const { data: tenants } = useTenants();
  const activeTenantName = tenants?.find((t) => t.tenantId === selectedTenantId)?.displayName ?? null;
  const scrollRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);

  const groups = useSettingsSections({ experimentalCount: EXPERIMENTAL_FLAG_COUNT });

  // Default to 'general' on first open; respect initialSection when caller
  // wants to deep-link into a specific pane.
  const [selected, setSelected] = useState<SettingsModalSection>(initialSection ?? 'general');
  const [query, setQuery] = useState('');

  // Reset to the default section each time the modal opens so users always
  // land on "general" (or the caller's deep-link target) instead of whatever
  // section they were viewing last time.
  useEffect(() => {
    if (open) {
      setSelected(initialSection ?? 'general');
    }
  }, [open, initialSection]);

  useEffect(() => {
    if (open) {
      closeButtonRef.current?.focus();
    }
  }, [open]);

  // Whenever the selected section changes, scroll the content back to top
  // so users don't land mid-scroll on a fresh section. `scrollTo` doesn't
  // exist on jsdom's Element prototype; guard with a feature test instead
  // of optional chaining the method call itself (which TS can't narrow).
  useEffect(() => {
    const el = scrollRef.current;
    if (el && typeof el.scrollTo === 'function') {
      el.scrollTo({ top: 0 });
    }
  }, [selected]);

  // Flatten once: needed for the heading lookup. With only two groups it's
  // trivial but keeps the API stable for future additions.
  const allSections = useMemo(() => groups.flatMap((g) => g.sections), [groups]);
  const currentSection = useMemo(
    () => allSections.find((s) => s.id === selected) ?? null,
    [allSections, selected],
  );

  const initialsSource = account?.name ?? account?.username;
  const userInitials = initialsSource
    ? initialsSource.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2)
    : '?';

  return (
    <Dialog open={open} modalType="modal" onOpenChange={(_, data) => { if (!data.open) onClose(); }}>
      <DialogSurface className={styles.surface} aria-labelledby="settings-modal-title">
        <div className={styles.body}>
          <aside className={styles.side} aria-label="Settings navigation">
            <div className={styles.profile}>
              <Avatar
                name={account?.name ?? account?.username ?? 'User'}
                initials={userInitials}
                size={36}
                color="brand"
                {...(photo ? { image: { src: photo } } : {})}
              />
              <div className={styles.profileText}>
                <span className={styles.profileName}>
                  {account?.name ?? account?.username ?? 'Signed out'}
                </span>
                {activeTenantName && (
                  <span className={styles.profileHandle}>{activeTenantName}</span>
                )}
              </div>
            </div>
            <SettingsNav
              groups={groups}
              selected={selected}
              onSelect={setSelected}
              query={query}
              onQueryChange={setQuery}
            />
          </aside>

          <section className={styles.content}>
            <header className={styles.contentHead}>
              <div className={styles.contentHeadTitleBlock}>
                <h2 id="settings-modal-title" className={styles.contentHeadTitle}>
                  {currentSection?.title ?? currentSection?.label ?? 'Settings'}
                </h2>
                <div className={styles.contentHeadCrumb}>
                  {currentSection?.crumb ?? ''}
                </div>
              </div>
              <Button
                ref={(el: HTMLButtonElement | null) => {
                  closeButtonRef.current = el;
                }}
                appearance="subtle"
                icon={<DismissRegular />}
                onClick={onClose}
                aria-label="Close settings"
              />
            </header>

            <div className={styles.scroll} ref={scrollRef}>
              {selected === 'profile' && <ProfileSection photo={photo} />}
              {selected === 'general' && (
                <GeneralSection onSwitchTenantClick={onSwitchTenantClick} />
              )}
              {selected === 'appearance' && <AppearanceSection />}
              {selected === 'accessibility' && (
                <AccessibilitySection onOpenShortcutsDialog={onOpenShortcutsDialog} />
              )}
              {selected === 'ai' && <AiAssistantSection />}
              {selected === 'privacy' && <PrivacySection />}
              {selected === 'experimental' && <ExperimentalSection />}
              {selected === 'about' && <AboutSection />}
            </div>

            <footer className={styles.footer}>
              <Text size={200}>Changes save automatically.</Text>
              <span className={styles.footerSpacer} />
              <Text size={200}>
                <kbd className={styles.kbd}>Esc</kbd> to close
                {' · '}
                {getShortcutParts('openSettings').map((part, i) => (
                  <kbd key={i} className={styles.kbd}>{part}</kbd>
                ))}{' '}
                to reopen
              </Text>
            </footer>
          </section>
        </div>
      </DialogSurface>
    </Dialog>
  );
};
